import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Paperclip, Upload, FileText, Download, Trash2, Pencil, Copy, CopyPlus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const OrderDetailDialog = ({
  showOrderDetail,
  setShowOrderDetail,
  attachments,
  uploading,
  handleUpdateOrderStatus,
  handleDownloadPDF,
  handleUploadAttachment,
  handleDeleteAttachment,
  openEditOrder,
  openSaveAsTemplate,
  setDuplicateTarget,
  setConfirmDelete,
  canManageTeam,
  phases,
  formatCurrency,
  formatDate,
  formatFileSize,
  getOrderStatusClass,
}) => (
  <Dialog open={!!showOrderDetail} onOpenChange={() => setShowOrderDetail(null)}>
    <DialogContent className="sm:max-w-2xl border border-zinc-200  rounded-none p-0 max-h-[90vh] overflow-y-auto">
      {showOrderDetail && (
        <>
          <DialogHeader className="p-6 pb-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-2xl font-heading font-bold uppercase tracking-wide">{showOrderDetail.vendor_name}</DialogTitle>
              <span className={`px-3 py-1 text-xs font-bold uppercase border border-zinc-200 ${getOrderStatusClass(showOrderDetail.status)}`}>
                {showOrderDetail.status}
              </span>
            </div>
          </DialogHeader>

          <div className="p-6 pt-4 space-y-4">
            {/* Order Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              {showOrderDetail.po_number && (
                <div>
                  <p className="text-xs font-bold uppercase text-zinc-500">PO Number</p>
                  <p className="font-mono">{showOrderDetail.po_number}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-bold uppercase text-zinc-500">Order Date</p>
                <p>{formatDate(showOrderDetail.order_date || showOrderDetail.created_at)}</p>
              </div>
              {showOrderDetail.expected_delivery_date && (
                <div>
                  <p className="text-xs font-bold uppercase text-zinc-500">Expected Delivery</p>
                  <p>{formatDate(showOrderDetail.expected_delivery_date)}</p>
                </div>
              )}
              {showOrderDetail.phase_id && phases.find(p => p.phase_id === showOrderDetail.phase_id) && (
                <div>
                  <p className="text-xs font-bold uppercase text-zinc-500">Phase</p>
                  <p>{phases.find(p => p.phase_id === showOrderDetail.phase_id)?.name}</p>
                </div>
              )}
            </div>

            {showOrderDetail.description && (
              <div>
                <p className="text-xs font-bold uppercase text-zinc-500 mb-1">Description</p>
                <p>{showOrderDetail.description}</p>
              </div>
            )}

            {/* Line Items */}
            <div>
              <p className="text-xs font-bold uppercase text-zinc-500 mb-2">Line Items</p>
              <div className="border border-zinc-200">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b-2 border-black">
                    <tr>
                      <th className="text-left p-2 font-bold">Product</th>
                      <th className="text-right p-2 font-bold">Qty</th>
                      <th className="text-right p-2 font-bold">Unit Price</th>
                      <th className="text-right p-2 font-bold">Extended</th>
                    </tr>
                  </thead>
                  <tbody>
                    {showOrderDetail.line_items.map((item, idx) => (
                      <tr key={item.line_item_id || `oi-${idx}`} className="border-b border-gray-200 last:border-b-0">
                        <td className="p-2">{item.product_description}</td>
                        <td className="p-2 text-right">{item.quantity} {item.unit}</td>
                        <td className="p-2 text-right">{formatCurrency(item.unit_price)}</td>
                        <td className="p-2 text-right font-bold">{formatCurrency(item.extended_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totals */}
            <div className="bg-zinc-50 border border-zinc-200 p-4">
              <div className="flex justify-between text-sm mb-1">
                <span>Subtotal:</span>
                <span className="font-bold">{formatCurrency(showOrderDetail.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm mb-1">
                <span>Tax:</span>
                <span className="font-bold">{formatCurrency(showOrderDetail.tax)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span>Freight:</span>
                <span className="font-bold">{formatCurrency(showOrderDetail.freight)}</span>
              </div>
              <div className="flex justify-between text-xl border-t-2 border-black pt-2">
                <span className="font-heading font-bold uppercase tracking-wide">TOTAL:</span>
                <span className="font-heading font-bold uppercase tracking-wide">{formatCurrency(showOrderDetail.total)}</span>
              </div>
            </div>

            {/* Status Change */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs font-bold uppercase">Update Status:</span>
              <select
                value={showOrderDetail.status}
                onChange={(e) => {
                  handleUpdateOrderStatus(showOrderDetail.order_id, e.target.value);
                  setShowOrderDetail({ ...showOrderDetail, status: e.target.value });
                }}
                className={`h-10 px-3 text-xs font-bold uppercase border border-zinc-200 ${getOrderStatusClass(showOrderDetail.status)}`}
              >
                <option value="Draft">Draft</option>
                <option value="Ordered">Ordered</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Shipped">Shipped</option>
                <option value="Delivered">Delivered</option>
                <option value="Issue">Issue</option>
              </select>
            </div>

            {/* Attachments */}
            <div className="pt-4 border-t-2 border-zinc-300" data-testid="order-attachments-section">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 flex items-center gap-1.5">
                  <Paperclip className="w-4 h-4" /> Attachments ({attachments.length})
                </p>
                <label className={`inline-flex items-center gap-1.5 h-8 px-3 text-[10px] font-bold uppercase cursor-pointer transition-all ${
                  uploading ? 'bg-zinc-200 text-zinc-500' : 'bg-zinc-900 text-white hover:bg-zinc-800'
                }`} data-testid="upload-attachment-btn">
                  <Upload className="w-3 h-3" /> {uploading ? 'Uploading...' : 'Upload'}
                  <input type="file" multiple className="hidden" disabled={uploading}
                    accept="image/*,.pdf,.xlsx,.docx,.csv"
                    onChange={(e) => handleUploadAttachment(showOrderDetail.order_id, e.target.files)} />
                </label>
              </div>
              {/* Drop zone */}
              <div
                className="border-2 border-dashed border-zinc-300 p-3 mb-3 text-center transition-colors hover:border-primary"
                data-testid="drop-zone"
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/5'); }}
                onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-primary', 'bg-primary/5'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-primary', 'bg-primary/5');
                  handleUploadAttachment(showOrderDetail.order_id, e.dataTransfer.files);
                }}
              >
                <p className="text-xs text-zinc-400">Drag & drop files here</p>
              </div>
              {attachments.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {attachments.map(att => (
                    <div key={att.attachment_id} className="border border-zinc-200 bg-white p-2 group relative"
                         data-testid={`attachment-${att.attachment_id}`}>
                      {att.is_image ? (
                        <div className="w-full h-20 bg-zinc-100 mb-1.5 overflow-hidden flex items-center justify-center">
                          <img src={`${BACKEND_URL}${att.url}`} alt={att.filename}
                            className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      ) : (
                        <div className="w-full h-20 bg-zinc-100 mb-1.5 flex items-center justify-center">
                          <FileText className="w-8 h-8 text-zinc-300" />
                        </div>
                      )}
                      <p className="text-[10px] font-medium truncate" title={att.filename}>{att.filename}</p>
                      <p className="text-[9px] text-zinc-400">{formatFileSize(att.size)}</p>
                      <div className="absolute top-1 right-1 hidden group-hover:flex gap-0.5">
                        <a href={`${BACKEND_URL}${att.url}`} download={att.filename} target="_blank" rel="noreferrer"
                          data-testid={`download-att-${att.attachment_id}`}
                          className="w-6 h-6 bg-white border border-zinc-200 flex items-center justify-center hover:bg-zinc-50">
                          <Download className="w-3 h-3" />
                        </a>
                        <button onClick={() => handleDeleteAttachment(att.attachment_id, showOrderDetail.order_id)}
                          data-testid={`delete-att-${att.attachment_id}`}
                          className="w-6 h-6 bg-white border border-zinc-200 flex items-center justify-center hover:bg-red-50 hover:text-red-600">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t-2 border-black flex-wrap">
              <button
                onClick={() => handleDownloadPDF(showOrderDetail.order_id)}
                data-testid="download-pdf-btn"
                className="h-10 px-4 bg-primary text-zinc-900 font-bold uppercase border border-zinc-200 hover:bg-primary-hover flex items-center gap-2 text-xs"
              >
                <Download className="w-4 h-4" />
                PDF
              </button>
              <button
                onClick={() => openEditOrder(showOrderDetail)}
                data-testid="edit-order-btn"
                className="h-10 px-4 bg-zinc-900 text-white font-bold uppercase border border-zinc-200 hover:bg-zinc-800 flex items-center gap-2 text-xs"
              >
                <Pencil className="w-4 h-4" />
                Edit
              </button>
              {canManageTeam && (
                <button
                  onClick={() => openSaveAsTemplate(showOrderDetail)}
                  data-testid="save-as-template-btn"
                  className="h-10 px-4 bg-white text-zinc-900 font-bold uppercase border-2 border-black hover:bg-zinc-100 flex items-center gap-2 text-xs"
                  title="Save as reusable template"
                >
                  <Copy className="w-4 h-4" />
                  Save as Template
                </button>
              )}
              {canManageTeam && (
                <button
                  onClick={() => setDuplicateTarget({ order: showOrderDetail, target_project_id: '', target_phase_id: '' })}
                  data-testid="duplicate-order-btn"
                  className="h-10 px-4 bg-white text-zinc-900 font-bold uppercase border-2 border-black hover:bg-primary flex items-center gap-2 text-xs"
                  title="Duplicate this order into another project"
                >
                  <CopyPlus className="w-4 h-4" />
                  Duplicate to…
                </button>
              )}
              <button
                onClick={() => setShowOrderDetail(null)}
                data-testid="close-order-detail-btn"
                className="flex-1 h-10 bg-white text-zinc-900 font-bold uppercase border border-zinc-200"
              >
                Close
              </button>
              <button
                onClick={() => setConfirmDelete({ type: 'order', id: showOrderDetail.order_id, name: showOrderDetail.po_number || showOrderDetail.description })}
                data-testid="delete-order-btn"
                className="h-10 px-6 bg-white text-destructive font-bold uppercase border border-zinc-200 hover:bg-destructive hover:text-white flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);
